import styled from '@emotion/styled'

import { LIST_SIZE } from '@/blocks/constants'
import { Display, HeadingL, HeadingM } from '@/components/Typography'
import type { TitleProps } from '@/models'
import { CONFIG } from '@/styles/emotion.config'

export interface Props {
  items: {
    highlightedNumber: string
    titleAfterNumber: string
    subtitleAfterNumber: string
  }[]
  title?: TitleProps
  id?: string
  size?: 'm' | 'l'
}

export const StepsComponent = styled.div`
  padding-top: 5rem;
  padding-bottom: 5rem;
  background-color: rgb(var(--theme-palegray));

  ${CONFIG.media.mobileLg} {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
`

export const StepsStyleBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;

  .block-steps {
    display: flex;
    border: 2px solid rgba(var(--theme-primary));
    border-radius: 1.5rem;
    background-color: rgba(var(--theme-snow));
    padding: 1.5rem;
    margin: 0.5rem 1rem;
    max-width: 14rem;
    justify-content: right;

    ${CONFIG.media.mobileLg} {
      align-items: center;
      max-width: 20rem;
      margin: 0.5rem auto;
    }
  }

  .text-area {
    max-width: 14rem;
    max-height: 8.5rem;

    ${CONFIG.media.mobileLg} {
      max-width: 100%;
    }

    .steps__title {
      font-size: 1.25rem;
      line-height: 1.5rem;
      margin-bottom: 0.5rem;
    }

    .steps__subtitle {
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.25rem;
    }
  }

  ${CONFIG.media.lg} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    margin-top: 1rem;
  }
`

export const StepsNumbers = styled.p`
  font-size: 11rem;
  opacity: 0.1;
  position: absolute;
  font-weight: 700;
  transform: translate(1rem, -1.5rem);

  ${CONFIG.media.mobileLg} {
    font-size: 6rem;
    transform: translate(1rem, -0.5rem);
  }
`

const Steps = ({ items, title, size = 'm' }: Props) => {
  const TitleComponent = size === LIST_SIZE.lg ? Display : HeadingL

  return (
    <StepsComponent>
      <div className="ecs-container">
        <div>
          {title && title.text && (
            <TitleComponent dark="high" className="eu-sm-2" tag={title.tag || 'h1'}>
              {title.text}
            </TitleComponent>
          )}
        </div>
        <StepsStyleBlock>
          {items?.map((item, index) => (
            <div className="block-steps" key={`bg-${index}`}>
              <div className="text-area">
                <HeadingM className="steps__title" color="dark-high" aria-hidden>
                  {item.titleAfterNumber}
                </HeadingM>
                <HeadingM className="steps__subtitle" color="dark-high" aria-hidden>
                  {item.subtitleAfterNumber}
                </HeadingM>
              </div>
              <StepsNumbers color="dark-low" aria-hidden>
                {item.highlightedNumber}
              </StepsNumbers>
            </div>
          ))}
        </StepsStyleBlock>
      </div>
    </StepsComponent>
  )
}

export default Steps
